import React, {useCallback} from 'react';

import {BulkActionDownload, BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {UserLink} from 'src/components/links';
import {RowNumbered} from 'src/components/row-numbered';
import {getGroupMemberBulkResult, getUserGroupUpdateProgress} from 'src/openapi/admin/apis/BulkGroupAssignApi';
import {useAppDispatch} from 'src/store/hooks';

import {Group} from '@pb/kit-component-group';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';


import {FILE_HEADERS, FILE_NAME, TOOL_TITLE} from './constants';

import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {UserGroupUpdateItem} from 'src/openapi/admin/models/UserGroupUpdateItem';

type Props = {
    id: string,
    onClose: () => void,
};

export const UpdateUserGroupResultModal = ({id, onClose}: Props) => {
    const apiDispatch = useAppDispatch();

    const getPaginatedResults = useCallback(
        (bulkResult: UserGroupUpdateItem[], {currentPage, pageSize}: Pagination) => bulkResult
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((res: UserGroupUpdateItem, index) => (
                <Row key={index}>
                    <Cell width={50}>
                        <RowNumbered
                            maxRowNumber={bulkResult?.length || 0}
                            placeholder={(currentPage - 1) * pageSize + index + 1}
                        />
                    </Cell>
                    <Cell>
                        <UserLink
                            userId={res.userId || 0}
                            caption={res.userEmail}
                            blank={true}
                            place={`Bulk ${TOOL_TITLE} Result`}
                        />
                    </Cell>
                    <Cell>{!!res.oldGroupId && !res.oldGroupName ? 'DELETED' : res.oldGroupName}</Cell>
                    <Cell>{!!res.newGroupId && !res.newGroupName ? 'DELETED' : res.newGroupName}</Cell>
                    <Cell>
                        <CompleteMark errors={res.errors}/>
                    </Cell>
                </Row>
            )),
        [],
    );

    return (
        <BulkActionResultModal<UserGroupUpdateItem>
            id={id}
            onClose={onClose}

            toolTitle={TOOL_TITLE}
            description={bulkResult => (
                <Group justify="space-between" alignItems="center">
                    <b>Update results</b>
                    <BulkActionDownload
                        toolName={TOOL_TITLE}
                        fileName={FILE_NAME}
                        headers={FILE_HEADERS}
                        startTime={bulkResult.startTime}
                        body={bulkResult.itemList?.map(item => ({
                            ...item,
                            userId: item.userId?.toString(),
                            oldGroupId: item.oldGroupId?.toString(),
                            newGroupId: item.newGroupId?.toString(),
                            oldGroupName: !!item.oldGroupId && !item.oldGroupName ? 'DELETED' : item.oldGroupName,
                            newGroupName: !!item.newGroupId && !item.newGroupName ? 'DELETED' : item.newGroupName,
                            errors: item.errors?.join('. '),
                        })) as Record<string, string>[]}
                    />
                </Group>
            )}
            table={(bulkResult, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={56}><strong>#</strong></Cell>
                        <Cell width={200}>Email</Cell>
                        <Cell width={200}>Old Account Group</Cell>
                        <Cell width={200}>New Account Group</Cell>
                        <Cell>Action Status</Cell>
                    </Head>
                    <Body>
                        {getPaginatedResults(bulkResult, pagination)}
                    </Body>
                </Table>
            )}

            getProgress={() => apiDispatch(getUserGroupUpdateProgress({id}))}
            getResult={() => apiDispatch(getGroupMemberBulkResult({id}))}

            sortComparator={(a, b) => a.userEmail.localeCompare(b.userEmail)}
        />
    );
};
